import { dec, isCreditKind, isNoteKind } from '@platform/domain';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '../../api/client';
import { issueMessage } from '../../api/errors';
import { itemApi, salesApi } from '../../api/endpoints';
import {
  activityCodesQuery,
  branchesQuery,
  nextNumberQuery,
  organizationQuery,
  orgCurrenciesQuery,
  partyQuery,
  queryKeys,
  salesDocumentQuery,
  taxesQuery,
} from '../../api/queries';
import {
  isInvoiceKind,
  type ApiIssue,
  type Draft,
  type DraftLine,
  type InvoiceKind,
  type Item,
  type MockScenario,
  type NoteSource,
} from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { NARROW_QUERY, useMediaQuery } from '../../lib/hooks';
import { Button } from '../../ui/Button';
import { Callout, Skeleton } from '../../ui/Feedback';
import { focusField } from '../../ui/FormErrors';
import { Page, useDocumentTitle } from '../../ui/Layout';
import { Stepper } from '../../ui/Stepper';
import { useToast } from '../../ui/Toast';
import type { SavedCustomer } from '../parties/CustomerForm';
import { copyWarningsFor, reviewStepFor, useDocumentActions } from './actions';
import { VerdictReasons } from './detail/EtaVerdict';
import { BuyerStep, type BuyerChoice } from './editor/BuyerStep';
import { DetailsStep } from './editor/DetailsStep';
import { EditorHeader, editorTitle } from './editor/EditorHeader';
import type { LineInfo } from './editor/LineEditor';
import { LinesStep } from './editor/LinesStep';
import type { ReferenceLineOption } from './editor/ReferenceLinesSheet';
import { ReferenceStep } from './editor/ReferenceStep';
import { MailChoice, mailTargetOf, useMailChoice, type MailChoiceValue } from './editor/MailChoice';
import {
  ReviewBuyerSection,
  ReviewDetailsSection,
  ReviewLinesSection,
  ReviewReferenceSection,
  ReviewStep,
} from './editor/ReviewStep';
import { StepBar } from './editor/StepBar';
import {
  NOTE_STEPS,
  STEPS,
  fieldOfIssue,
  issueCounts,
  stepListCounts,
  stepTitlesOf,
  stepOfField,
  stepsFor,
  type EditorMode,
  type StepId,
} from './editor/steps';
import { SummaryRail, SummaryToggle } from './editor/SummaryRail';
import { useNoteReferences } from './editor/noteReferences';
import styles from './Sales.module.css';

interface EditorState {
  draft: Draft;
  kind: InvoiceKind;
  version: number;
  number: string | null;
  items: Record<string, LineInfo>;
  /** Block 11: the note's chosen references by id, held for step 1's display across the first save. */
  references?: Record<string, NoteSource>;
  scenario: MockScenario;
  copyWarnings?: ApiIssue[];
  /** Open the issue confirmation as soon as the editor is shown (save-then-issue on a new draft). */
  intent?: 'issue';
  /** The step to show: kept across the first save, a reload and "load the latest version". */
  step: StepId;
  /**
   * WP-1b.4: the step-4 choice of the buyer's e-mail copy. Editor state, never a draft field (M-4);
   * kept only across the hand-off of the first save, which "Issue and send" makes first.
   */
  mailChoice?: MailChoiceValue;
}

/** State handed from the "new" route to the edit route after the first save (no data loss). */
const handoff = new Map<string, EditorState>();

const emptyDraft = (): Draft => ({ currency: 'EGP', lines: [] });

function lineFromItem(item: Item, kind: InvoiceKind, currency: string): DraftLine {
  return {
    itemId: item.id,
    quantity: '1',
    ...(currency === 'EGP' ? { unitPrice: item.unitPrice } : {}),
    // TX-M2: no automatic tax on export lines.
    taxIds: kind === 'ei' ? [] : (item.defaultTaxIds ?? []),
    ...(item.defaultDiscountRate
      ? { discount: { rate: item.defaultDiscountRate } }
      : item.defaultDiscountAmount
        ? { discount: { amount: item.defaultDiscountAmount } }
        : {}),
    ...(item.defaultItemsDiscount ? { itemsDiscount: item.defaultItemsDiscount } : {}),
    ...(item.defaultValueDifference ? { valueDifference: item.defaultValueDifference } : {}),
  };
}

/**
 * Block 11: a note line is a referenced line. The locked fields (item, unit, taxes, code) come from
 * the reference; the draft carries only the link and the editable quantity and price, which default
 * to the reference's (a full reduction is the common case) and can be edited down.
 */
function lineFromReference(option: ReferenceLineOption): DraftLine {
  return {
    referencedLineId: option.line.id,
    quantity: dec(option.line.quantity || '0').toString(),
    unitPrice: dec(option.line.unitPrice || '0').toString(),
  };
}

const lineInfoOf = (item: {
  nameAr: string;
  etaItemType: string | null;
  etaItemCode: string | null;
  unitCode: string;
  unitPrice: string;
  currency: string;
}): LineInfo => ({
  name: item.nameAr,
  code: `${item.etaItemType ?? ''} ${item.etaItemCode ?? ''}`,
  unit: item.unitCode,
  price: item.unitPrice,
  priceCurrency: item.currency,
});

/** Strips empty strings so drafts never carry blank optional fields. */
function clean(draft: Draft): Draft {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(draft))
    if (v !== '' && v !== undefined && v !== null) out[k] = v;
  const lines = draft.lines.map((l) => {
    const o: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(l)) if (v !== '' && v !== undefined && v !== null) o[k] = v;
    return o as DraftLine;
  });
  return { ...(out as unknown as Draft), lines };
}

/**
 * MF-INV-002, create and edit an invoice as a guided flow (BLOCK-1-REDESIGN §A.2, R-5): four
 * steps (Buyer · Document details · Invoice lines · Review and issue), a summary rail and a step
 * bar docked at the bottom. The step is component state, not part of the URL.
 */
export function InvoiceEditorPage({ mode }: { mode: 'new' | 'edit' }) {
  const { t } = useI18n();
  const { orgId = '', docId } = useParams({ strict: false }) as { orgId?: string; docId?: string };
  const search = useSearch({ strict: false }) as { kind?: string; from?: string };
  const navigate = useNavigate();
  const detail = useQuery({
    ...salesDocumentQuery(orgId, docId ?? ''),
    enabled: mode === 'edit' && Boolean(docId),
  });
  // "Load the latest version" keeps the step the user was on.
  const reloadStep = useRef<StepId | null>(null);

  const [state, setState] = useState<EditorState | null>(() => {
    if (mode === 'new') {
      // Block 11: `?kind=c|d|ec|ed` opens a note; anything else is a domestic invoice. A note
      // opened `?from=<invoiceId>` starts with that invoice chosen (step 1), and so begins there.
      const initialKind: InvoiceKind =
        search.kind && isInvoiceKind(search.kind) ? search.kind : 'i';
      const note = isNoteKind(initialKind);
      const from = note && typeof search.from === 'string' && search.from ? search.from : null;
      return {
        draft: from ? { ...emptyDraft(), references: [from] } : emptyDraft(),
        kind: initialKind,
        version: 0,
        number: null,
        items: {},
        references: {},
        scenario: 'valid',
        step: note ? 'reference' : 'buyer',
      };
    }
    const h = docId ? handoff.get(docId) : undefined;
    if (h && docId) handoff.delete(docId);
    return h ?? null;
  });

  // Edit mode: initialise from the server once (or again after "load the latest version").
  useEffect(() => {
    if (mode !== 'edit' || state || !detail.data) return;
    const d = detail.data;
    if (d.document.status !== 'draft') {
      void navigate({
        to: '/orgs/$orgId/sales/invoices/$docId',
        params: { orgId, docId: d.document.id },
        replace: true,
      });
      return;
    }
    const items: Record<string, LineInfo> = {};
    for (const l of d.lines)
      if (l.itemId)
        items[l.itemId] = {
          name: l.description,
          code: `${l.itemType} ${l.itemCode}`,
          unit: l.unitType,
          // A saved document carries no catalogue price; the editor offers one only when it has
          // just read the item (RI-05).
          price: null,
          priceCurrency: null,
        };
    // Lines saved without explicit taxes use the item's defaults on the server: show them as chips.
    const draft = d.draft ?? emptyDraft();
    const lines = draft.lines.map((l, i) =>
      l.taxIds
        ? l
        : {
            ...l,
            taxIds: (d.lines[i]?.taxes ?? []).flatMap((t) =>
              t.organizationTaxId ? [t.organizationTaxId] : [],
            ),
          },
    );
    // «افتح خطوة المراجعة» from an issue confirmation (WP-1b.4) opens the review step. A reopened
    // draft otherwise opens at its first blocking step, in the mode's own step order (a note's
    // step 1 is the reference, not the buyer).
    const atReview = reviewStepFor.delete(d.document.id);
    const entrySteps = isNoteKind(d.document.kind) ? NOTE_STEPS : STEPS;
    const entryCounts = issueCounts(d.readiness?.issues ?? []);
    const firstBlocking = entrySteps.find((s) => s !== 'review' && entryCounts[s] > 0) ?? 'review';
    const step = reloadStep.current ?? (atReview ? 'review' : firstBlocking);
    reloadStep.current = null;
    setState({
      draft: { ...draft, lines },
      // Block 11 data bug: a reopened credit/debit-note draft must keep its stored kind, or the
      // next save would send `kind:'i'` and silently turn the note into a domestic invoice.
      kind: isInvoiceKind(d.document.kind) ? d.document.kind : 'i',
      version: d.document.version,
      number: d.document.number,
      items,
      references: {},
      scenario: 'valid',
      copyWarnings: d.document.copyWarnings,
      step,
    });
  }, [mode, state, detail.data, navigate, orgId]);

  useDocumentTitle(editorTitle(t, state?.number ?? null, state?.kind ?? 'i'));

  if (!state)
    return (
      <Page>
        <Skeleton height="10rem" />
      </Page>
    );
  return (
    <Editor
      key={docId ?? 'new'}
      orgId={orgId}
      docId={docId ?? null}
      initial={state}
      copiedFrom={detail.data?.document.copiedFrom?.number ?? null}
      supersedes={detail.data?.document.supersedes ?? null}
      onReload={async (step) => {
        reloadStep.current = step;
        await detail.refetch();
        setState(null);
      }}
    />
  );
}

/** Where focus goes after a render: the step's heading, a field, the rail's issues, or nowhere. */
type FocusTarget =
  { kind: 'heading' } | { kind: 'field'; name: string } | { kind: 'readiness' } | { kind: 'none' };

function Editor({
  orgId,
  docId,
  initial,
  copiedFrom,
  supersedes,
  onReload,
}: {
  orgId: string;
  docId: string | null;
  initial: EditorState;
  copiedFrom: string | null;
  /** WP-1b.2: the refused document this correction draft replaces. */
  supersedes: { id: string; number: string | null } | null;
  onReload: (step: StepId) => Promise<void>;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const access = useAccess(orgId);
  const org = useQuery(organizationQuery(orgId));
  const branches = useQuery(branchesQuery(orgId));
  const activities = useQuery(activityCodesQuery(orgId));
  const currencies = useQuery(orgCurrenciesQuery(orgId));
  const taxes = useQuery(taxesQuery(orgId));

  const [draft, setDraftState] = useState<Draft>(initial.draft);
  const [kind, setKind] = useState<InvoiceKind>(initial.kind);
  const [version, setVersion] = useState(initial.version);
  const [number, setNumber] = useState<string | null>(initial.number);
  const [items, setItems] = useState<Record<string, LineInfo>>(initial.items);
  // Block 11: the chosen references' display data, by id (step 1); the draft keeps only the ids.
  const [references, setReferences] = useState<Record<string, NoteSource>>(
    initial.references ?? {},
  );
  const [scenario, setScenario] = useState<MockScenario>(initial.scenario);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [conflict, setConflict] = useState(false);
  // Step 1: how a buyer is being chosen, and whether the chosen buyer's form is open (M-05).
  const [buyerChoice, setBuyerChoice] = useState<BuyerChoice>('records');
  const [buyerEditing, setBuyerEditing] = useState(false);
  const copyWarnings: ApiIssue[] =
    (docId ? copyWarningsFor.get(docId) : undefined) ?? initial.copyWarnings ?? [];

  // Steps, focus and the inline-message rule (§A.2 "Per-step validation").
  const [step, setStep] = useState<StepId>(initial.step);
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; seq: number } | null>(
    null,
  );
  const [announcement, setAnnouncement] = useState('');
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [leftSteps, setLeftSteps] = useState<ReadonlySet<StepId>>(() => new Set());
  const [showAll, setShowAll] = useState(initial.step === 'review');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const readinessRef = useRef<HTMLDivElement>(null);
  // Below 64rem the rail is a one-line summary that expands in place (expanded on the review).
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const [railChoice, setRailChoice] = useState<{ step: StepId; open: boolean } | null>(null);

  // WP-1b.4: an issue refused for the buyer's address re-reads the buyer and resets the choice.
  const mailRefused = useRef<() => void>(() => undefined);
  const actions = useDocumentActions(orgId, {
    onIssued: (id) =>
      void navigate({ to: '/orgs/$orgId/sales/invoices/$docId', params: { orgId, docId: id } }),
    onMailRefused: () => mailRefused.current(),
  });

  // Block 11: an editor is an invoice (step 1 the buyer) or a note (step 1 the reference, step 3
  // «بنود الإشعار»). The kind is fixed for a note: it comes from the URL and has no buyer step.
  const mode: EditorMode = isNoteKind(kind) ? 'note' : 'invoice';
  const editorSteps = stepsFor(mode);
  const stepTitles = stepTitlesOf({
    buyer: t.editor.sections.customer,
    reference: t.editor.steps.reference,
    details: t.editor.steps.details,
    lines: mode === 'note' ? t.editor.steps.noteLines : t.editor.sections.lines,
    review: t.editor.steps.review,
  });
  // Block 11 (role-4 M-2, m-3): a note's references resolved through `note-basis` — the line facts
  // the step-4 review table shows and the reference sources the buyer/reference rows read, robust to
  // a reopened draft whose picked-sources state is empty. Empty (no query) for an invoice.
  const { refLines: noteLineInfo, sources: resolvedSources } = useNoteReferences(
    orgId,
    kind,
    mode === 'note' ? (draft.references ?? []) : [],
  );

  // WP-1b.4: a reset of the e-mail choice made on another step is said again on arriving at step 4.
  const mailResetPending = useRef(false);
  const goTo = (next: StepId, target: FocusTarget = { kind: 'heading' }) => {
    if (next !== step) {
      setLeftSteps((left) => new Set(left).add(step));
      if (next === 'review') setShowAll(true);
      setStep(next);
      const arrived = t.editor.steps.announce(
        String(editorSteps.indexOf(next) + 1),
        String(editorSteps.length),
        stepTitles[next],
      );
      const resetNote = next === 'review' && mailResetPending.current;
      if (next === 'review') mailResetPending.current = false;
      setAnnouncement(resetNote ? `${arrived}. ${t.editor.mail.reset}` : arrived);
    }
    setFocusRequest((previous) => ({ target, seq: (previous?.seq ?? 0) + 1 }));
  };
  // Focus moves once the step has rendered: to its heading, or to the field a fix link names.
  useEffect(() => {
    if (!focusRequest) return;
    const { target } = focusRequest;
    if (target.kind === 'none') return;
    if (target.kind === 'readiness' && readinessRef.current) {
      readinessRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      readinessRef.current.focus();
      return;
    }
    if (target.kind === 'field' && focusField(target.name)) return;
    headingRef.current?.focus();
  }, [focusRequest]);

  // Defaults (2.01): primary activity code and default branch, filled once on a new document.
  useEffect(() => {
    if (docId) return;
    setDraftState((d) => {
      let next = d;
      const primary = activities.data?.find((a) => a.isPrimary)?.code;
      if (!d.activityCode && primary) next = { ...next, activityCode: primary };
      const branch =
        branches.data?.find((b) => b.is_default && b.status === 'active') ??
        branches.data?.find((b) => b.status === 'active');
      if (!d.branchId && branch) next = { ...next, branchId: branch.id };
      return next;
    });
  }, [docId, activities.data, branches.data]);

  const markTouched = (...paths: string[]) =>
    setTouched((set) => {
      const next = new Set(set);
      for (const p of paths) next.add(p);
      return next;
    });
  const setDraft = (update: (d: Draft) => Draft) => {
    setDraftState(update);
    setDirty(true);
  };
  const setField = <K extends keyof Draft>(key: K, value: Draft[K] | undefined) => {
    markTouched(String(key));
    setDraft((d) => {
      const entries = Object.entries(d).filter(([k]) => k !== key);
      if (!(value === undefined || value === '' || value === null)) entries.push([key, value]);
      return Object.fromEntries(entries) as unknown as Draft;
    });
  };
  const setLine = (i: number, patch: Partial<DraftLine>) => {
    markTouched(...Object.keys(patch).map((k) => `lines.${i}.${k}`));
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l, j) => {
        if (j !== i) return l;
        return Object.fromEntries(
          Object.entries({ ...l, ...patch }).filter(([, v]) => v !== undefined && v !== ''),
        ) as DraftLine;
      }),
    }));
  };

  // Unsaved-changes guard for the browser tab.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Live server preview (totals + readiness), debounced; the server stays authoritative.
  const body = useMemo(() => clean(draft), [draft]);
  const [debounced, setDebounced] = useState(body);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(body), 350);
    return () => clearTimeout(h);
  }, [body]);
  const preview = useQuery({
    queryKey: [...queryKeys.org(orgId), 'sales', 'preview', kind, JSON.stringify(debounced)],
    queryFn: ({ signal }) => salesApi.preview(orgId, kind, debounced, signal),
    placeholderData: keepPreviousData,
  });
  const next = useQuery({ ...nextNumberQuery(orgId, kind), enabled: !docId });

  const applyScenario = async (id: string, s: MockScenario) => {
    if (org.data?.etaEnvironment === 'mock' && s !== 'valid')
      await salesApi.setMockScenario(orgId, id, s);
  };

  const save = useMutation({
    mutationFn: async () =>
      docId
        ? salesApi.update(orgId, docId, version, body, kind)
        : salesApi.create(orgId, body, kind),
    onSuccess: async (res) => {
      setConflict(false);
      setDirty(false);
      setSavedAt(new Date());
      setVersion(res.version);
      setNumber(res.number);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales', 'list'] });
      if (!docId) await applyScenario(res.id, scenario);
      return res;
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'version_conflict') setConflict(true);
      else toast.error(error);
    },
  });

  const saveDraft = async (intent?: 'issue') => {
    const res = await save.mutateAsync();
    if (!docId) {
      // Hand the state, including the step, to the edit route so nothing is lost.
      handoff.set(res.id, {
        draft,
        kind,
        version: res.version,
        number: res.number,
        items,
        references,
        scenario,
        step,
        mailChoice,
        ...(intent ? { intent } : {}),
      });
      void navigate({
        to: '/orgs/$orgId/sales/invoices/$docId/edit',
        params: { orgId, docId: res.id },
        replace: true,
      });
    } else toast.success(t.editor.saved);
    return res;
  };
  const canSave = access.can(docId ? 'invoice.edit' : 'invoice.create');
  // D-22 / R-12 / §10: a credit note (c/ec) needs `invoice.cancel` on top of issue + submit, so the
  // sales role can draft one but not issue it. Drafting and editing stay governed by `canSave`
  // above — the cancel key gates only issuing. Debit notes and invoices are unaffected.
  const canIssue =
    access.can('invoice.issue') &&
    access.can('invoice.submit') &&
    (!isCreditKind(kind) || access.can('invoice.cancel'));
  const needsCancelPermission = isCreditKind(kind) && !access.can('invoice.cancel');

  // Ctrl/⌘+S saves (any keyboard layout).
  const saveShortcut = useRef<() => void>(() => undefined);
  saveShortcut.current = () => {
    if (canSave && !save.isPending) void saveDraft().catch(() => undefined);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.code === 'KeyS' || e.key === 's')) {
        e.preventDefault();
        saveShortcut.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const receiverPartyId =
    draft.receiver && 'partyId' in draft.receiver ? draft.receiver.partyId : null;
  const receiverParty = useQuery({
    ...partyQuery(orgId, receiverPartyId ?? ''),
    enabled: Boolean(receiverPartyId),
  });

  // WP-1b.4: «نسخة للمشتري بالبريد» on step 4. The recipient is the buyer record's address (read
  // only); the choice resets to «لا تُرسِل نسخة» whenever the recipient changes, with a polite note.
  const mailTarget = mailTargetOf(draft.receiver, receiverPartyId ? receiverParty.data : null);
  const [mailChoice, setMailChoice] = useMailChoice(
    initial.mailChoice ?? 'none',
    mailTarget,
    () => {
      setAnnouncement(t.editor.mail.reset);
      mailResetPending.current = step !== 'review';
    },
  );
  mailRefused.current = () => {
    setMailChoice('none');
    void receiverParty.refetch();
  };
  const mailFor = () =>
    mailTarget?.state === 'available'
      ? { send: mailChoice === 'send', recipient: mailTarget.recipient }
      : { send: false, recipient: null };

  const openIssue = (res: {
    id: string;
    number: string | null;
    version: number;
    readiness: string;
  }) => {
    if (res.readiness !== 'ready') {
      // A refused attempt shows every field's message and moves focus to the list of issues
      // (on narrow screens the summary opens first).
      setShowAll(true);
      setRailChoice({ step, open: true });
      setFocusRequest((previous) => ({
        target: { kind: 'readiness' },
        seq: (previous?.seq ?? 0) + 1,
      }));
      return;
    }
    actions.requestIssue({
      id: res.id,
      number: res.number,
      version: res.version,
      kind,
      receiverName: receiverParty.data?.name ?? null,
      cash: Boolean(draft.receiver && 'cash' in draft.receiver),
      totalAmount: preview.data?.preview.result?.totalAmount ?? null,
      mail: mailFor(),
    });
  };

  const onIssue = async () => {
    if (!docId || dirty) {
      const res = await saveDraft(docId ? undefined : 'issue');
      if (docId) openIssue({ ...res, readiness: res.readiness });
      return;
    }
    const fresh = await salesApi.preview(orgId, kind, body);
    openIssue({ id: docId, number, version, readiness: fresh.readiness });
  };

  // A new draft that was saved by "Issue and send" continues straight to the confirmation.
  useEffect(() => {
    if (initial.intent === 'issue' && docId) void onIssue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = preview.data?.preview.result;
  const issues = preview.data?.preview.issues ?? [];
  const warnings = preview.data?.preview.warnings ?? [];
  const counts = issueCounts(issues);
  const listCounts = stepListCounts(counts);
  /** A field shows its message once edited, once its step was left, on the review, or after a refused issue. */
  const issueOf = (path: string) => {
    const issue = issues.find((x) => x.path === path);
    if (!issue) return undefined;
    const visible =
      showAll ||
      leftSteps.has(stepOfField(path)) ||
      [...touched].some((f) => path === f || path.startsWith(`${f}.`));
    return visible ? issueMessage(t, issue) : undefined;
  };
  const isMock = org.data?.etaEnvironment === 'mock';

  const onBuyerSaved = (party: SavedCustomer) => {
    setBuyerChoice('records');
    setBuyerEditing(false);
    setField('receiver', { partyId: party.id });
    void queryClient.invalidateQueries({
      queryKey: [...queryKeys.org(orgId), 'sales', 'preview'],
    });
  };

  // Block 11 step 1: a chosen reference is kept by id in the draft and by display data here. The
  // first choice sets the currency the rest of the note is taken from (notes-editor.md:84-89).
  const onPickReference = (source: NoteSource) => {
    setReferences((m) => ({ ...m, [source.id]: source }));
    markTouched('references');
    setDraft((d) => {
      const ids = d.references ?? [];
      if (ids.includes(source.id)) return d;
      return {
        ...d,
        references: [...ids, source.id],
        ...(ids.length === 0 ? { currency: source.currency } : {}),
      };
    });
  };
  const onTakeOutReference = (id: string) => {
    markTouched('references');
    setDraft((d) => ({ ...d, references: (d.references ?? []).filter((x) => x !== id) }));
  };

  const onRailIssue = (issue: ApiIssue) => {
    // A saved buyer's problem is fixed in the buyer itself (M-05).
    if (issue.path.startsWith('receiver') && receiverPartyId && issue.issue !== 'unknown_party') {
      setBuyerEditing(true);
      goTo('buyer', { kind: 'field', name: 'name' });
      return;
    }
    goTo(stepOfField(issue.path), { kind: 'field', name: fieldOfIssue(issue.path) });
  };

  const onCurrency = (v: string) => {
    if (v === draft.currency) return;
    markTouched('currency');
    // CT-B1: a price typed in one currency is never re-read as another; the unit prices are
    // cleared and must be entered in the new currency.
    setDraft((d) => ({
      ...d,
      currency: v,
      ...(v === 'EGP' ? { exchangeRate: undefined } : {}),
      lines: d.lines.map((l) =>
        Object.fromEntries(Object.entries(l).filter(([k]) => k !== 'unitPrice')),
      ) as DraftLine[],
    }));
    if (draft.lines.some((l) => l.unitPrice)) toast.success(t.editor.pricesCleared);
  };

  const railOpen = railChoice && railChoice.step === step ? railChoice.open : step === 'review';
  const rail = (
    <SummaryRail
      ref={readinessRef}
      orgId={orgId}
      number={number}
      nextNumber={next.data?.number ?? null}
      issues={issues}
      warnings={warnings}
      result={result}
      currency={draft.currency}
      amountDueForeign={preview.data?.preview.amountDueForeign ?? null}
      onIssue={onRailIssue}
      isCredit={isCreditKind(kind)}
      isNote={mode === 'note'}
      referenceStepLabel={mode === 'note' ? stepTitles.reference : undefined}
    />
  );
  const stepIndex = editorSteps.indexOf(step);
  const position = stepIndex + 1;
  const back = stepIndex > 0 ? (editorSteps[stepIndex - 1] ?? null) : null;
  const forward = editorSteps[stepIndex + 1] ?? null;

  return (
    <Page>
      <EditorHeader
        orgId={orgId}
        number={number}
        kind={kind}
        environment={org.data?.etaEnvironment}
      />

      {copiedFrom ? (
        <Callout tone="info" role="note">
          <span data-testid="copied-banner">{t.detail.copiedFromBanner(copiedFrom)}</span>
        </Callout>
      ) : null}
      {supersedes ? <SupersedesBanner orgId={orgId} supersedes={supersedes} /> : null}
      {copyWarnings.length ? (
        <Callout tone="warning" title={t.editor.copyWarnings}>
          <ul data-testid="copy-warnings">
            {copyWarnings.map((w, i) => (
              <li key={i}>{issueMessage(t, w)}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {conflict ? (
        <Callout
          tone="danger"
          role="alert"
          title={t.editor.conflictTitle}
          action={
            <Button variant="secondary" onPress={() => void onReload(step)} testId="load-latest">
              {t.editor.loadLatest}
            </Button>
          }
        >
          <span data-testid="conflict-message">{t.editor.conflictBody}</span>
        </Callout>
      ) : null}

      <Stepper<StepId>
        label={t.editor.steps.label}
        steps={editorSteps.map((id) => ({ id, title: stepTitles[id], issues: listCounts[id] }))}
        current={step}
        onSelect={(id) => goTo(id)}
        nameOf={(s, n, total) =>
          `${t.editor.steps.announce(String(n), String(total), s.title)} — ${
            s.issues ? t.editor.readiness.incomplete(s.issues) : t.editor.steps.clean
          }`
        }
        testId="editor-steps"
        itemTestId={(id) => `step-${id}`}
      />
      <p className="visually-hidden" aria-live="polite" data-testid="editor-announcer">
        {announcement}
      </p>

      <div className={styles.editorBody}>
        {isNarrow ? (
          <div className={styles.railSummary}>
            <SummaryToggle
              number={number ?? next.data?.number ?? null}
              amountDue={result?.totalAmount ?? null}
              issues={issues.length}
              isExpanded={railOpen}
              onToggle={() => setRailChoice({ step, open: !railOpen })}
            />
            {railOpen ? rail : null}
          </div>
        ) : null}
        <section
          className={styles.stepPanel}
          aria-labelledby="editor-step-title"
          data-testid="step-panel"
          data-step={step}
        >
          <h2 id="editor-step-title" ref={headingRef} tabIndex={-1} className={styles.stepTitle}>
            <span className={styles.stepCounter}>
              {t.editor.steps.counter(String(position), String(editorSteps.length))}
            </span>
            {stepTitles[step]}
          </h2>
          {step === 'reference' ? (
            <ReferenceStep
              orgId={orgId}
              kind={kind}
              references={draft.references ?? []}
              picked={references}
              issueOf={issueOf}
              onPick={onPickReference}
              onTakeOut={onTakeOutReference}
            />
          ) : step === 'buyer' ? (
            <BuyerStep
              orgId={orgId}
              draft={draft}
              kind={kind}
              onKind={(k) => {
                markTouched('kind');
                setKind(k);
                setDirty(true);
              }}
              receiverParty={receiverParty.data ?? null}
              issueOf={issueOf}
              onReceiver={(receiver) => {
                if (!receiver) {
                  setBuyerChoice('records');
                  setBuyerEditing(false);
                }
                setField('receiver', receiver);
              }}
              choice={buyerChoice}
              onChoice={setBuyerChoice}
              isEditing={buyerEditing}
              onEditing={setBuyerEditing}
              onSaved={onBuyerSaved}
            />
          ) : step === 'details' ? (
            <DetailsStep
              orgId={orgId}
              draft={draft}
              kind={kind}
              mode={mode}
              issueOf={issueOf}
              setField={setField}
              onCurrency={onCurrency}
              organization={org.data}
              branches={branches.data}
              activities={activities.data}
              currencies={currencies.data}
            />
          ) : step === 'lines' ? (
            <LinesStep
              orgId={orgId}
              draft={draft}
              kind={kind}
              mode={mode}
              items={items}
              result={result}
              taxes={taxes.data ?? []}
              issueOf={issueOf}
              canEditItem={access.can('item.edit')}
              onBackToReference={() => goTo('reference')}
              onAddReferenceLines={(picked) => {
                markTouched('lines');
                setDraft((d) => ({
                  ...d,
                  lines: [...d.lines, ...picked.map(lineFromReference)],
                }));
              }}
              onAdd={(picked) => {
                setItems((m) => {
                  const nextItems = { ...m };
                  for (const it of picked) nextItems[it.id] = lineInfoOf(it);
                  return nextItems;
                });
                markTouched('lines');
                setDraft((d) => ({
                  ...d,
                  lines: [...d.lines, ...picked.map((it) => lineFromItem(it, kind, d.currency))],
                }));
              }}
              onLine={setLine}
              onPickItem={(i, item) => {
                setItems((m) => ({ ...m, [item.id]: lineInfoOf(item) }));
                markTouched(`lines.${i}.itemId`);
                setDraft((d) => ({
                  ...d,
                  lines: d.lines.map((l, j) =>
                    j === i ? lineFromItem(item, kind, d.currency) : l,
                  ),
                }));
              }}
              onRemove={(i) =>
                setDraft((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }))
              }
              onItemSaved={(lineIndex, id) => {
                // M-05: the line follows the edited item (name and EGP price from the catalogue).
                void itemApi.get(orgId, id).then((d) => {
                  setItems((m) => ({ ...m, [id]: lineInfoOf(d) }));
                  if (draft.currency === 'EGP') setLine(lineIndex, { unitPrice: d.unitPrice });
                  void queryClient.invalidateQueries({
                    queryKey: [...queryKeys.org(orgId), 'sales', 'preview'],
                  });
                });
              }}
              onExtraDiscount={(v) => setField('extraDiscountAmount', v)}
            />
          ) : (
            <ReviewStep
              counts={counts}
              titles={stepTitles}
              onGoTo={(id) => goTo(id)}
              order={
                mode === 'note'
                  ? (['reference', 'details', 'lines'] as const)
                  : (['buyer', 'details', 'lines'] as const)
              }
              permissionNotice={
                needsCancelPermission ? t.editor.note.needsCancelPermission : undefined
              }
              sections={{
                ...(mode === 'note'
                  ? {
                      reference: (
                        <ReviewReferenceSection
                          ids={draft.references ?? []}
                          // m-3: resolved sources back the buyer and reference rows even on a
                          // reopened draft; the session's picked sources win where both have one.
                          picked={{ ...resolvedSources, ...references }}
                        />
                      ),
                    }
                  : {
                      buyer: (
                        <ReviewBuyerSection
                          draft={draft}
                          kind={kind}
                          party={receiverParty.data ?? null}
                        />
                      ),
                    }),
                details: (
                  <ReviewDetailsSection
                    draft={draft}
                    organization={org.data}
                    branch={branches.data?.find((b) => b.id === draft.branchId)}
                    activity={activities.data?.find((a) => a.code === draft.activityCode)}
                  />
                ),
                lines: (
                  <ReviewLinesSection
                    draft={draft}
                    items={items}
                    taxes={taxes.data ?? []}
                    result={result}
                    noteLines={noteLineInfo}
                  />
                ),
              }}
              mail={
                // Block 11 (role-4 M-3): a note refuses the buyer e-mail copy (R-3) and has no
                // buyer chooser, so the block (which would read «اختر المشتري أولًا») never renders
                // on a note editor; the invoice path is unchanged.
                canIssue && mode === 'invoice' ? (
                  <MailChoice
                    target={mailTarget}
                    choice={mailChoice}
                    onChoice={setMailChoice}
                    transport={org.data?.emailTransport}
                    canEditBuyer={access.can('customer.edit')}
                    onFixBuyer={() => {
                      // M-05: the buyer form opens in edit mode with its e-mail field focused.
                      setBuyerEditing(true);
                      goTo('buyer', { kind: 'field', name: 'email' });
                    }}
                  />
                ) : undefined
              }
              isMock={isMock}
              scenario={scenario}
              onScenario={(s) => {
                setScenario(s);
                if (docId)
                  void salesApi
                    .setMockScenario(orgId, docId, s)
                    .catch((e: unknown) => toast.error(e));
              }}
            />
          )}
        </section>

        {isNarrow ? null : rail}
      </div>

      <StepBar
        saveState={
          save.isPending
            ? t.editor.saving
            : dirty || !docId
              ? t.editor.unsaved
              : savedAt
                ? t.editor.savedAt(savedAt.toLocaleTimeString())
                : t.editor.saved
        }
        isSaved={!dirty && Boolean(docId)}
        backTo={back ? stepTitles[back] : null}
        nextTo={forward ? stepTitles[forward] : null}
        onBack={() => back && goTo(back)}
        onNext={() => forward && goTo(forward)}
        canSave={canSave}
        isSaving={save.isPending}
        onSave={() => void saveDraft().catch(() => undefined)}
        canIssue={canIssue}
        onIssue={() => void onIssue().catch(() => undefined)}
      />

      {actions.elements}
    </Page>
  );
}

/**
 * A correction draft (WP-1b.2, MF-INV-009): which refused document it replaces (linked), and what
 * the Tax Authority reported on that document — labelled as the Authority's, apart from the local
 * checks in the summary rail.
 */
function SupersedesBanner({
  orgId,
  supersedes,
}: {
  orgId: string;
  supersedes: { id: string; number: string | null };
}) {
  const { t } = useI18n();
  const replaced = useQuery(salesDocumentQuery(orgId, supersedes.id));
  const number = supersedes.number ?? '';
  return (
    <Callout tone="warning" role="note">
      <p data-testid="supersedes-banner">
        {t.editor.supersedesBanner}{' '}
        <Link
          to="/orgs/$orgId/sales/invoices/$docId"
          params={{ orgId, docId: supersedes.id }}
          data-testid="supersedes-link"
        >
          {number}
        </Link>
        . {t.editor.supersedesHint}
      </p>
      {replaced.data ? (
        <>
          <p className={styles.verdictReasonsTitle}>{t.editor.supersedesReasons(number)}</p>
          <VerdictReasons detail={replaced.data} />
        </>
      ) : null}
    </Callout>
  );
}
